import { Component, Input, OnInit } from '@angular/core';
import { THEMES } from 'src/app/data/product/product.data';
import { Product } from 'src/app/model/product/product.model';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

interface ThemeItem {
  key: string;
  name: string;
  appearance: string;
  category: string;
  colors: string[];
  logo: string;
}

// Blog widget: pick a Codigrate theme and read the palette it is built from. The
// theme list comes straight from the product data (every theme with a palette), so
// it stays in sync with the catalogue. Each swatch copies its hex, and the whole
// set opens in the palette tool. Used by the "Anatomy of a Light Theme" post; the
// markdown marks a mount point with <!--THEME-START-->...<!--THEME-END-->.
@Component( {
  selector: 'app-theme-palette',
  templateUrl: './theme-palette.component.html',
  styleUrls: [ './theme-palette.component.scss' ],
} )
export class ThemePaletteComponent implements OnInit {

  @Input() heading = 'Pick A Theme';
  // Pre-selected theme key (e.g. 'everest'). Falls back to the first theme.
  @Input() theme = 'everest';

  themes: ThemeItem[] = [];
  selectedKey = '';
  copiedHex = '';

  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  ngOnInit(): void {
    const norm = ( h: string ): string => ( '#' + ( h || '' ).replace( /^#/, '' ) ).toUpperCase();
    const all = THEMES
      .map( line => line.products )
      .reduce( ( a, b ) => a.concat( b ), [] as Product[] );

    this.themes = all
      .filter( p => !!p && !!p.appearance && !p.isBundle && !!p.palette && p.palette.length >= 3 )
      .map( p => ( {
        key: p.key,
        name: p.name,
        appearance: p.appearance || '',
        category: p.category || '',
        // Always the theme's full ten-colour ramp, so every theme shows ten squares.
        colors: ( p.palette || [] ).slice( 0, 10 ).map( norm ),
        logo: p.logo || '',
      } ) )
      .sort( ( a, b ) => a.name.localeCompare( b.name ) );

    this.selectedKey = this.themes.some( t => t.key === this.theme )
      ? this.theme
      : ( this.themes[ 0 ]?.key || '' );
  }

  get selected(): ThemeItem | undefined {
    return this.themes.find( t => t.key === this.selectedKey );
  }

  // Options for the custom dropdown, each carrying the theme's logo as its icon.
  get themeOptions(): { value: string; label: string; icon: string }[] {
    return this.themes.map( t => ( { value: t.key, label: t.name, icon: t.logo } ) );
  }

  get colors(): string[] {
    return this.selected?.colors || [];
  }

  // The palette tool route for the current theme's colours.
  get paletteUrl(): string {
    return '/tools/palette/' + this.colors.map( c => c.replace( '#', '' ) ).join( '-' );
  }

  onSelect( key: string ): void {
    this.selectedKey = key;
    this.copiedHex = '';
    this.track();
  }

  copy( hex: string ): void {
    if ( navigator?.clipboard ) {
      navigator.clipboard.writeText( hex ).catch( () => { /* ignore */ } );
    }
    this.copiedHex = hex;
    this.track();
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'theme-palette' } );
  }
}
